import styled from 'astroturf/react';
import { Modal } from 'js/design_system/Modal';
import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import React, { useContext } from 'react';
import {
  Paper,
  OpenalexWork,
  FollowableUser,
  News,
  isPaper,
} from '../shared/types';
import PaperAbstract from './PaperAbstract';
import { ImpressionPropsGenerator } from '../ImpressionHelper';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import PaperInteractions from './PaperInteractions';
import useNewsInteractions from '../News/useNewsInteractions';
import { FeedContext } from '../contexts/feed';

const ModalWrapper = styled.div`
  max-width: 800px;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled(Heading)`
  margin-top: -20px;
  margin-bottom: 20px;
  font-weight: 300;
`;

const ModalBody = styled(Body)`
  margin-bottom: 40px;
  font-weight: 300;
  display: flex;
  flex-direction: column;
`;

const ModalPapers = styled.div`
  display: flex;
  flex-direction: column;
  gap: 35px;
`;

const RoundedCorners = styled.div`
  & > * {
    border-radius: var(--border-radius-radius-md);
  }
`;

type Props = News & {
  getUserData: (id: number) => FollowableUser;
  index: number;
};

const PaperWithInteractions = ({
  paper,
  props,
  impressionPropsGen,
}: {
  paper: Paper | OpenalexWork;
  props: Props;
  impressionPropsGen: ImpressionPropsGenerator;
}) => {
  const { newsActions } = useContext(FeedContext);
  const { actions, permissions } = useNewsInteractions({
    ...props,
    work: paper,
    messageText: {
      bookmarkSuccess: 'Saved to library',
      bookmarkFailure: 'Failed to save to library',
      unbookmarkSuccess: 'Removed from library',
      unbookmarkFailure: 'Failed to remove from library',
    },
    ...newsActions,
    setUserFollowing: () => {},
  });

  return (
    <RoundedCorners>
      <PaperAbstract
        work={paper}
        canUseReader={false}
        showReader={() => {}}
        impressionPropsGen={impressionPropsGen()()}
        paperInteractions={
          <PaperInteractions
            work={paper}
            showBookmark={true}
            aiButton={undefined}
            canUseReader={false}
            showReader={() => {}}
            isSnippet={false}
            isBookmarked={!!permissions.isBookmarked}
            onBookmark={actions.bookmarkWork}
            onUnbookmark={actions.unbookmarkWork}
            onExplain={() => {}}
            impressionPropsGen={
              isPaper(paper)
                ? impressionPropsGen(ImpressionEntity.Work, paper.id)()
                : impressionPropsGen(ImpressionEntity.OpenalexWork, paper.id)()
            }
          />
        }
      />
    </RoundedCorners>
  );
};

export function MultiPaperModal({
  open,
  onClose,
  heading,
  body,
  papers,
  impressionPropsGen,
  props,
}: {
  open: boolean;
  onClose: () => void;
  heading: string;
  body: string;
  papers: Array<Paper | OpenalexWork>;
  impressionPropsGen: ImpressionPropsGenerator;
  props: Props;
}) {
  return (
    <Modal open={open} onClose={onClose}>
      <ModalWrapper>
        <ModalHeader size="lg">{heading}</ModalHeader>
        <ModalBody size="lg">{body}</ModalBody>
        <ModalPapers>
          {papers.map((paper) => (
            <PaperWithInteractions
              key={paper.id}
              paper={paper}
              props={props}
              impressionPropsGen={impressionPropsGen}
            />
          ))}
        </ModalPapers>
      </ModalWrapper>
    </Modal>
  );
}
